// `@adobe/alloy` is pinned to 2.31.1 in package.json (not a caret range).
// Versions >= 2.32.0 are pure ESM with only an `exports` field and no
// `main`/`module`, which webpack 4 cannot resolve. Bump only after the
// frontend moves to webpack 5 (which understands `exports`).
import { createInstance } from '@adobe/alloy';

import { getAdobeConfigValue, isLocalDev } from 'utils/environment';

export const isAdobeInitialized = () => typeof window !== 'undefined' && Boolean(window.alloy);

/**
 * Initialize Adobe Alloy SDK and load Adobe Launch script.
 * Should only be called once when user first grants consent.
 */
const init = async () => {
  if (window.Cypress || isAdobeInitialized()) {
    return;
  }

  const adobeConfig = getAdobeConfigValue();
  if (!adobeConfig) {
    // Adobe not configured for this environment/region
    return;
  }

  const alloyInstance = createInstance({ name: 'alloy' });
  const { edgeConfigId, orgId, dtmScriptUrl } = adobeConfig;

  await alloyInstance('configure', {
    edgeConfigId,
    orgId,
    debugEnabled: isLocalDev,
    defaultConsent: 'in', // User has already consented when init is called
    // `personalizationStorageEnabled` intentionally NOT set (defaults off): we
    // want every page load to re-evaluate against the current language so cards
    // react to the language toggle. Persisting made them stick to the first
    // language seen.
  });

  window.alloy = alloyInstance;

  // Broadcast readiness so consumers that mounted before consent (e.g.
  // `useContentCards`) can pick up the SDK without polling.
  if (typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new Event('adobe-alloy-ready'));
  }

  const dtmScript = document.createElement('script');
  dtmScript.type = 'text/javascript';
  dtmScript.async = true;
  dtmScript.src = dtmScriptUrl;

  const script = document.getElementsByTagName('script')[0];
  script?.parentNode?.insertBefore(dtmScript, script);
};

/**
 * Update Adobe consent state.
 * Call this when user changes their consent preferences.
 * @param {boolean} hasConsent - Whether user has granted targeting consent
 */
export const setAdobeConsent = async hasConsent => {
  if (window.Cypress || !isAdobeInitialized()) {
    return;
  }

  await window.alloy('setConsent', {
    consent: [
      {
        standard: 'Adobe',
        version: '2.0',
        value: {
          collect: {
            val: hasConsent ? 'y' : 'n',
          },
        },
      },
    ],
  });
};

export default init;
